import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DollarSign,
  Plus,
  Trash2,
  Edit,
  Calendar,
  TrendingDown,
  FolderOpen,
  Loader2,
  Receipt,
  PieChart,
  RefreshCw,
  Repeat,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "CARD", label: "Card" },
  { value: "EFT", label: "EFT/Bank Transfer" },
  { value: "CHEQUE", label: "Cheque" },
];

const CHART_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6",
  "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
  "#f43f5e", "#64748b", "#84cc16", "#0ea5e9", "#a855f7"
];

export const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [yearlySummary, setYearlySummary] = useState(null);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialogs
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("expense"); // expense or category
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    category_id: "",
    description: "",
    payment_method: "CASH",
    reference_number: "",
    notes: "",
    is_recurring: false,
    recurring_day: 1,
  });
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDesc, setNewCategoryDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [generatingRecurring, setGeneratingRecurring] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
    fetchMonthlySummary();
    fetchYearlySummary();
  }, []);

  useEffect(() => {
    fetchMonthlySummary();
    fetchExpenses();
  }, [selectedMonth, selectedYear]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expenses/categories`);
      setCategories(response.data);
      
      // Seed categories if empty
      if (response.data.length === 0) {
        await axios.post(`${API}/expenses/seed-categories`);
        const seeded = await axios.get(`${API}/expenses/categories`);
        setCategories(seeded.data);
        toast.success("Default expense categories created");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
      const endMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
      const endYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
      const endDate = `${endYear}-${String(endMonth).padStart(2, "0")}-01`;
      
      const response = await axios.get(`${API}/expenses`, {
        params: { start_date: startDate, end_date: endDate, limit: 500 }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await axios.get(`${API}/expenses/summary/monthly`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlySummary(response.data);
    } catch (error) {
      console.error("Error fetching monthly summary:", error);
    }
  };

  const fetchYearlySummary = async () => {
    try {
      const response = await axios.get(`${API}/expenses/summary/yearly`, {
        params: { year: selectedYear }
      });
      setYearlySummary(response.data);
    } catch (error) {
      console.error("Error fetching yearly summary:", error);
    }
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm({
      date: new Date().toISOString().split("T")[0],
      amount: "",
      category_id: "",
      description: "",
      payment_method: "CASH",
      reference_number: "",
      notes: "",
    });
    setExpenseDialogOpen(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date,
      amount: expense.amount.toString(),
      category_id: expense.category_id,
      description: expense.description,
      payment_method: expense.payment_method,
      reference_number: expense.reference_number || "",
      notes: expense.notes || "",
      is_recurring: expense.is_recurring || false,
      recurring_day: expense.recurring_day || 1,
    });
    setExpenseDialogOpen(true);
  };

  const handleGenerateRecurring = async () => {
    setGeneratingRecurring(true);
    try {
      const response = await axios.post(`${API}/expenses/generate-recurring`, null, {
        params: { year: selectedYear, month: selectedMonth }
      });
      
      if (response.data.generated_count > 0) {
        toast.success(`Generated ${response.data.generated_count} recurring expense(s)`);
        fetchExpenses();
        fetchMonthlySummary();
      } else if (response.data.skipped_count > 0) {
        toast.info("All recurring expenses already exist for this month");
      } else {
        toast.info("No recurring expenses set up yet. Mark an expense as 'Recurring Monthly' when creating it.");
      }
    } catch (error) {
      console.error("Error generating recurring expenses:", error);
      toast.error("Failed to generate recurring expenses");
    } finally {
      setGeneratingRecurring(false);
    }
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.amount || !expenseForm.category_id || !expenseForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
      };

      if (editingExpense) {
        await saveWithRetry(() => axios.put(`${API}/expenses/${editingExpense.id}`, data));
        toast.success("Expense updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/expenses`, data));
        toast.success("Expense added");
      }

      setExpenseDialogOpen(false);
      fetchExpenses();
      fetchMonthlySummary();
      fetchYearlySummary();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error(errorMessage(error, "Failed to save expense"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setSaving(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/expenses/categories`, {
        name: newCategoryName.trim(),
        description: newCategoryDesc.trim() || null,
      }));
      toast.success(`Category "${newCategoryName}" added`);
      setNewCategoryName("");
      setNewCategoryDesc("");
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add category"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setSaving(true);
    try {
      if (deleteType === "expense") {
        await axios.delete(`${API}/expenses/${itemToDelete.id}`);
        toast.success("Expense deleted");
        fetchExpenses();
        fetchMonthlySummary();
        fetchYearlySummary();
      } else {
        const response = await axios.delete(`${API}/expenses/categories/${itemToDelete.id}`);
        if (response.data.soft_delete) {
          toast.success(response.data.message);
        } else {
          toast.success("Category deleted");
        }
        fetchCategories();
      }
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `R ${(value || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="p-6 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-red-600" />
            Business Expenses
          </h1>
          <p className="text-gray-600 mt-1">Track and manage your business expenses</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleGenerateRecurring}
            disabled={generatingRecurring}
            className="border-green-400 text-green-700 hover:bg-green-50"
          >
            {generatingRecurring ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Generate Monthly
          </Button>
          <Button variant="outline" onClick={() => setCategoryDialogOpen(true)}>
            <FolderOpen className="h-4 w-4 mr-2" />
            Manage Categories
          </Button>
          <Button onClick={handleAddExpense} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-gray-500" />
            <span className="font-medium">Period:</span>
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((name, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map((year) => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Monthly Expenses</p>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(monthlySummary?.total_expenses || 0)}
                </p>
              </div>
              <TrendingDown className="h-10 w-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monthlySummary?.total_count || 0}
                </p>
              </div>
              <Receipt className="h-10 w-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Categories Used</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monthlySummary?.by_category?.length || 0}
                </p>
              </div>
              <PieChart className="h-10 w-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">YTD Expenses</p>
                <p className="text-2xl font-bold text-orange-700">
                  {formatCurrency(yearlySummary?.total_expenses || 0)}
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="expenses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="expenses">Expense List</TabsTrigger>
          <TabsTrigger value="breakdown">Category Breakdown</TabsTrigger>
          <TabsTrigger value="trends">Monthly Trends</TabsTrigger>
        </TabsList>

        {/* Expense List Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <CardTitle>
                Expenses - {monthNames[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No expenses recorded for this period</p>
                  <Button onClick={handleAddExpense} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Expense
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell className="font-mono text-sm">
                          {new Date(expense.date).toLocaleDateString("en-ZA")}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {expense.category_name}
                            </span>
                            {expense.is_recurring && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1">
                                <Repeat className="h-3 w-3" />
                                Monthly
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={expense.description}>
                          {expense.description}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {expense.payment_method}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {expense.reference_number || "-"}
                        </TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(expense.amount)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditExpense(expense)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(expense, "expense")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Category Breakdown Tab */}
        <TabsContent value="breakdown">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Expense Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {monthlySummary?.by_category?.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={monthlySummary.by_category}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="total_amount"
                          nameKey="category_name"
                          label={({ category_name, percentage }) => 
                            `${category_name.substring(0, 10)}${category_name.length > 10 ? '..' : ''} (${percentage}%)`
                          }
                          labelLine={false}
                        >
                          {monthlySummary.by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-gray-500">
                    No expense data for this period
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>By Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {monthlySummary?.by_category?.map((cat, i) => (
                    <div key={cat.category_id} className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <span className="font-medium">{cat.category_name}</span>
                          <span className="font-bold text-red-600">{formatCurrency(cat.total_amount)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{cat.count} transactions</span>
                          <span>{cat.percentage}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full mt-1">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${cat.percentage}%`,
                              backgroundColor: CHART_COLORS[i % CHART_COLORS.length]
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!monthlySummary?.by_category || monthlySummary.by_category.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No expenses this month</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Monthly Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Expense Trends - {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent>
              {yearlySummary?.monthly_data ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={yearlySummary.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month_name" />
                      <YAxis tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="total_amount" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? "Edit Expense" : "Add New Expense"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Category *</Label>
              <Select
                value={expenseForm.category_id}
                onValueChange={(v) => setExpenseForm({ ...expenseForm, category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                placeholder="What was this expense for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={expenseForm.payment_method}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...PAYMENT_METHODS].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map((pm) => (
                      <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference # (Optional)</Label>
                <Input
                  placeholder="Invoice/Receipt number"
                  value={expenseForm.reference_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, reference_number: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Additional notes..."
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* Recurring Expense Option */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="is_recurring"
                  checked={expenseForm.is_recurring}
                  onCheckedChange={(checked) => setExpenseForm({ ...expenseForm, is_recurring: checked })}
                />
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="is_recurring" className="font-medium text-blue-700 cursor-pointer">
                    Recurring Monthly Expense
                  </Label>
                </div>
              </div>
              {expenseForm.is_recurring && (
                <div className="ml-7 text-sm text-blue-600">
                  <p>This expense will be saved as a template. Click the &quot;Generate Monthly&quot; button to automatically create this expense each month.</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs">Day of month:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={expenseForm.recurring_day}
                      onChange={(e) => setExpenseForm({ ...expenseForm, recurring_day: parseInt(e.target.value) || 1 })}
                      className="w-16 h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveExpense} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingExpense ? "Update" : "Add"} Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Expense Categories</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add New Category */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <Label className="font-semibold">Add New Category</Label>
              <Input
                placeholder="Category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
              <Input
                placeholder="Description (optional)"
                value={newCategoryDesc}
                onChange={(e) => setNewCategoryDesc(e.target.value)}
              />
              <Button onClick={handleAddCategory} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Category
              </Button>
            </div>

            {/* Category List */}
            <div>
              <Label className="font-semibold">Existing Categories ({categories.length})</Label>
              <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg">
                {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-gray-50"
                  >
                    <div>
                      <span className="font-medium">{cat.name}</span>
                      {cat.description && (
                        <p className="text-xs text-gray-500">{cat.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(cat, "category")}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete {deleteType === "expense" ? "Expense" : "Category"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {deleteType === "expense" ? itemToDelete?.description : itemToDelete?.name}
              </span>
              ?
            </p>
            {deleteType === "expense" && (
              <p className="text-sm text-gray-500 mt-2">
                Amount: {formatCurrency(itemToDelete?.amount)}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
